import { useCallback, useEffect } from 'react';
import { RotateCw, X } from 'lucide-react';
import { DOMINIOS, TipoFalhaEnvio, formatarDataHora, formatarTamanho, type DetalheEnvioDto, type EnvioComFalhaDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { useCarga } from './campos';

const descricoes = (nome: string): Record<number, string> => DOMINIOS.find((d) => d.nome === nome)?.descricoes ?? {};
export const TIPOS_FALHA = descricoes('TipoFalhaEnvio');
export const PROVEDORES = descricoes('ProvedorEnvio');
export const CANAIS = descricoes('CanalEnvio');
export const SITUACOES = descricoes('SituacaoEnvio');

const EVENTOS = descricoes('TipoEventoEnvio');
const TIPOS_DESTINATARIO = descricoes('TipoDestinatario');

/** Pílula do tipo da falha, com o selo de reenvio. */
export function SeloFalha({ e }: { e: Pick<EnvioComFalhaDto, 'tipoFalha' | 'reenviavel'> }) {
  const tipo = e.tipoFalha ?? TipoFalhaEnvio.NaoClassificado;
  const naoClassificado = tipo === TipoFalhaEnvio.NaoClassificado;
  return (
    <span className="config__envio-selos">
      <span className={`pilula ${naoClassificado ? 'pilula--aguardando' : 'pilula--informacao'}`}>{TIPOS_FALHA[tipo]}</span>
      {e.reenviavel === false && <span className="pilula pilula--urgencia">Não adianta reenviar</span>}
    </span>
  );
}


function Dados({ d }: { d: DetalheEnvioDto }) {
  return (
    <dl className="config__envio-dados">
      <dt>Canal</dt>
      <dd>
        {CANAIS[d.canal]}
        {d.provedor && ` · ${PROVEDORES[d.provedor]}`}
      </dd>
      <dt>Situação</dt>
      <dd>{SITUACOES[d.situacao]}</dd>
      <dt>Empresa</dt>
      <dd>{d.empresa?.nome ?? 'Sem empresa (do sistema)'}</dd>
      <dt>Categoria</dt>
      <dd>{d.categoria ?? '—'}</dd>
      <dt>Pedido por</dt>
      <dd>{d.criadoPor ?? 'O sistema'}</dd>
      <dt>Criado em</dt>
      <dd>{formatarDataHora(d.criadoEm)}</dd>
      {d.remetente && (
        <>
          <dt>Remetente</dt>
          <dd>{d.remetente}</dd>
        </>
      )}
      {d.responderPara && (
        <>
          <dt>Responder para</dt>
          <dd>{d.responderPara}</dd>
        </>
      )}
    </dl>
  );
}

function Destinatarios({ d }: { d: DetalheEnvioDto }) {
  const lista = d.enderecos.length ? d.enderecos : d.destinatarios.map((endereco) => ({ endereco, tipo: null, situacao: d.situacao }));
  return (
    <ul className="config__envio-lista">
      {lista.map((e) => (
        <li key={`${e.tipo}-${e.endereco}`}>
          {e.tipo !== null && <small>{TIPOS_DESTINATARIO[e.tipo]}:</small>} {e.endereco} <span className="pilula pilula--informacao">{SITUACOES[e.situacao]}</span>
        </li>
      ))}
    </ul>
  );
}

function Historico({ d }: { d: DetalheEnvioDto }) {
  return (
    <ol className="config__envio-lista">
      {d.historico.map((t) => (
        <li key={t.numero}>
          <strong>
            {t.numero}ª tentativa · {formatarDataHora(t.ocorridaEm)}
            {t.provedor && ` · ${PROVEDORES[t.provedor]}`}
          </strong>
          {t.sucesso ? <span className="pilula pilula--sucesso">Enviada</span> : <span className="pilula pilula--aguardando">{t.tipoFalha ? TIPOS_FALHA[t.tipoFalha] : 'Falhou'}</span>}
          {t.erro && <small>{t.erro}</small>}
        </li>
      ))}
      {d.eventos.map((ev, i) => (
        <li key={`ev-${i}`}>
          <strong>
            {EVENTOS[ev.tipo]} · {formatarDataHora(ev.ocorridoEm)}
          </strong>
          {(ev.destinatario || ev.motivo) && <small>{[ev.destinatario, ev.motivo].filter(Boolean).join(': ')}</small>}
        </li>
      ))}
    </ol>
  );
}

function Cadeia({ d, abrir }: { d: DetalheEnvioDto; abrir: (uuid: string) => void }) {
  if (d.cadeia.length < 2) return null;
  return (
    <section>
      <h3>Original e reenvios</h3>
      <ol className="config__envio-lista">
        {d.cadeia.map((c) => (
          <li key={c.uuid}>
            <button type="button" className="botao botao--secundario" aria-current={c.uuid === d.uuid} disabled={c.uuid === d.uuid} onClick={() => abrir(c.uuid)}>
              {c.numeroReenvio === 0 ? 'Original' : `Reenvio ${c.numeroReenvio}`}
            </button>{' '}
            {formatarDataHora(c.criadoEm)} · {SITUACOES[c.situacao]}
            {c.criadoPor && ` · por ${c.criadoPor}`}
            {c.motivo && <small>{c.motivo}</small>}
          </li>
        ))}
      </ol>
    </section>
  );
}

function Anexos({ d }: { d: DetalheEnvioDto }) {
  if (d.anexos.length === 0) return null;
  return (
    <>
      <h3>Anexos</h3>
      <ul className="config__envio-lista">
        {d.anexos.map((a) => (
          <li key={a.nome}>
            {a.nome} <small>{formatarTamanho(a.tamanhoBytes)}</small>
          </li>
        ))}
      </ul>
    </>
  );
}

function Corpo({ d }: { d: DetalheEnvioDto }) {
  if (d.corpoOculto) return <p className="config__nota">O conteúdo do convite não aparece: ele leva um link de acesso.</p>;
  if (d.corpoHtml) return <iframe className="config__envio-corpo" title="Conteúdo da mensagem" sandbox="" srcDoc={d.corpoHtml} />;
  return <pre className="config__envio-texto">{d.corpoTexto ?? '—'}</pre>;
}

type Props = { uuid: string; abrir: (uuid: string) => void; fechar: () => void; reenviar: (e: EnvioComFalhaDto) => void };

/** Detalhe de um envio com falha: dados, destinatários, tentativas, eventos do provedor, cadeia de reenvios e conteúdo. Esc fecha. */
export function DetalheEnvio({ uuid, abrir, fechar, reenviar }: Props) {
  const { configuracoes } = useDependencias();
  const carregar = useCallback(() => configuracoes.envio(uuid), [configuracoes, uuid]);
  const { dados: d, erro } = useCarga(carregar);
  // Esc fecha só o detalhe (não as Configurações inteiras); com uma confirmação aberta, o Esc é dela.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('dialog[open]')) return;
      e.stopPropagation();
      fechar();
    };
    window.addEventListener('keydown', aoTeclar, true);
    return () => window.removeEventListener('keydown', aoTeclar, true);
  }, [fechar]);
  return (
    <aside className="config__envio-detalhe" aria-label="Detalhe do envio">
      <header>
        <h2>{d?.assunto ?? 'Envio'}</h2>
        <button type="button" className="botao-icone" aria-label="Fechar o detalhe" onClick={fechar}>
          <X size={18} />
        </button>
      </header>
      {erro && <p className="cartao-acesso__erro">{erro}</p>}
      {!d && !erro && <p>Carregando…</p>}
      {d && (
        <>
          <SeloFalha e={d} />
          {d.motivo && <p className="config__envio-motivo">{d.motivo}</p>}
          {d.podeReenviar && (
            <button type="button" className="botao botao--primario" onClick={() => reenviar(d)}>
              <RotateCw size={14} aria-hidden /> Reenviar
            </button>
          )}
          {d.originalUuid && (
            <button type="button" className="botao botao--secundario" onClick={() => abrir(d.originalUuid!)}>
              Este é o reenvio {d.numeroReenvio}: abrir a original
            </button>
          )}
          <Dados d={d} />
          <h3>Destinatários</h3>
          <Destinatarios d={d} />
          <h3>Tentativas e eventos</h3>
          <Historico d={d} />
          <Cadeia d={d} abrir={abrir} />
          <Anexos d={d} />
          <h3>Conteúdo</h3>
          <Corpo d={d} />
        </>
      )}
    </aside>
  );
}
